import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { Auth } from "aws-amplify";
import "./../../assets/css/navbar.css";
import { HTTPService } from "./../../services/httpService";
import { isAllDealerships } from "../../util/locationUtils";
import ScopeService from "./../../services/scopeService";
import packageJSON from "../../../package.json";
import JobviousLogo from "./../../assets/Jobvious_white_logo_base.svg";
import JobviousFavicon from "./../../assets/jobvious_favicon.png";
import JJLogoSeparate from "./../../assets/jj_logos_separate.png";
import JJLogoOverlapping from "./../../assets/jj_logos_overlapping.png";

// New config-driven navigation imports
import { SECTION_ORDER } from "../../config/navConfig";
import { getMenuForRole } from "../../config/roleMenus";
import { loadExpansionState, saveExpansionState, getDefaultExpansionState, loadSidebarCollapsed, saveSidebarCollapsed } from "../../util/navStateUtils";
import NavSection from "../navigation/NavSection";

const API = new HTTPService();

const JJ_DEMO_EMAILS = ['installer@jobvious.com', 'dealer@jobvious.com'];
function isJJDemoUser() {
  const email = (ScopeService.getUser()?.Email || '').toLowerCase();
  return JJ_DEMO_EMAILS.includes(email);
}

class navBar extends Component {
  doSubmit = async () => {
    Auth.signOut()
      .then(() => {
        localStorage.clear();
        this.props.history.push("/login");
      })
      .catch((err) => {
        //console.log(err);
      });
  };

  state = {
    countsReturned: [], // Keep for backward compatibility
    requestCount: 0,
    jobCount: 0,
    scheduledCount: 0,
    inProgressCount: 0,
    pickupCount: 0,
    reviewCount: 0,
    referralCount: 0,
    myWorkCount: 0,
    tasksCount: 0,
    loading: true,
    error: null,
    isCalendarConnected: false,
    isStripeConnected: false,
    // Custom installer logo
    customLogoUrl: null,
    // Section expansion state
    expandedSections: getDefaultExpansionState(),
    // Submenu state (for Automations)
    submenuState: {
      automations: false
    },
    // Sidebar collapsed state
    sidebarCollapsed: false
  };

  loadCustomLogo = () => {
    const company = ScopeService.getCompany() || {};
    this.setState({ customLogoUrl: company.logo_url || null });
  };

  handleLogoUpdated = () => {
    this.loadCustomLogo();
  };

  async componentDidMount() {
    // Load custom logo from company data
    this.loadCustomLogo();

    // Load saved expansion state
    const savedExpansion = loadExpansionState(this.props.entityId, this.props.userRole);
    const savedCollapsed = loadSidebarCollapsed(this.props.entityId);
    this.setState({ expandedSections: savedExpansion, sidebarCollapsed: savedCollapsed });

    // Notify App.js of initial collapsed state
    window.dispatchEvent(new CustomEvent('sidebarToggled', { detail: { collapsed: savedCollapsed } }));

    // On a hard browser reload (Cmd+R / F5), bypass the server's count cache
    // so the user gets fresh numbers. Soft navigation within the SPA keeps
    // using the cache for performance.
    const navEntries = (typeof performance !== 'undefined' && performance.getEntriesByType)
      ? performance.getEntriesByType('navigation')
      : [];
    const isHardReload = navEntries[0]?.type === 'reload';

    try {
      await this.loadOptimizedNavbarData({ force: isHardReload });
    } catch (error) {
      console.error('Optimized navbar loading failed, falling back to legacy method:', error);
      await this.loadNavbarDataLegacy();
    }

    if (this.props.userType === "installer") {
      await this.checkCalendarStatus();
      await this.checkStripeStatus();

      if (this.isTechnician()) {
        await this.loadMyWorkCount();
      }
    }

    window.addEventListener('calendarStatusChanged', this.handleCalendarStatusChange);
    window.addEventListener('refreshNavbarCounts', this.handleRefreshNavbarCounts);
    window.addEventListener('logoUpdated', this.handleLogoUpdated);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.entityId !== this.props.entityId) {
      // Load expansion state for new company
      const savedExpansion = loadExpansionState(this.props.entityId, this.props.userRole);
      const savedCollapsed = loadSidebarCollapsed(this.props.entityId);
      this.setState({ expandedSections: savedExpansion, sidebarCollapsed: savedCollapsed });
      window.dispatchEvent(new CustomEvent('sidebarToggled', { detail: { collapsed: savedCollapsed } }));
    }
  }

  componentWillUnmount() {
    window.removeEventListener('calendarStatusChanged', this.handleCalendarStatusChange);
    window.removeEventListener('refreshNavbarCounts', this.handleRefreshNavbarCounts);
    window.removeEventListener('logoUpdated', this.handleLogoUpdated);
  }

  handleCalendarStatusChange = (event) => {
    this.setState({
      isCalendarConnected: event.detail.connected
    });
  };

  handleRefreshNavbarCounts = async () => {
    try {
      await this.loadOptimizedNavbarData({ force: true });
    } catch (error) {
      console.error('Failed to refresh navbar counts:', error);
      await this.loadNavbarDataLegacy();
    }
  };

  checkCalendarStatus = async () => {
    try {
      const response = await API.get(`/installerAuth/${this.props.entityId}`);
      if (response.data && response.data[0]) {
        const { authenticated, calendar_id } = response.data[0];
        this.setState({
          isCalendarConnected: authenticated && !!calendar_id
        });
      }
    } catch (error) {
      console.error('Error checking calendar status in navbar:', error);
    }
  };

  checkStripeStatus = async () => {
    try {
      const response = await API.get(`/installers/${this.props.entityId}`);
      if (response.data && response.data[0]) {
        const { stripe_account_id, onboarding_complete } = response.data[0];
        this.setState({
          isStripeConnected: !!stripe_account_id && !!onboarding_complete
        });
      }
    } catch (error) {
      console.error('Error checking Stripe status in navbar:', error);
    }
  };

  loadMyWorkCount = async () => {
    try {
      const response = await API.get(`/requestService/technician/${this.props.userId}`);
      if (response.data && Array.isArray(response.data)) {
        const activeServices = response.data.filter(s => s.service_state !== 'finished');
        this.setState({ myWorkCount: activeServices.length });
      }
    } catch (error) {
      console.error('Error loading My Work count:', error);
    }
  };


  loadOptimizedNavbarData = async ({ force = false } = {}) => {
    try {
      const locationParam = this.props.selectedLocationId ? `&locationId=${this.props.selectedLocationId}` : '';
      const roleParam = this.props.userRole ? `&role=${this.props.userRole}` : '';
      const groupParam = this.props.groupId ? `&groupId=${this.props.groupId}` : '';
      const dealerParam = isAllDealerships(this.props.selectedDealerId)
        ? '' : `&dealerId=${this.props.selectedDealerId}`;
      // fresh=1 tells the server to bypass its cache and recompute. Used on
      // manual refresh actions (refresh button, refreshNavbarCounts event)
      // and on hard browser reloads. Cached for normal page navigation.
      const freshParam = force ? '&fresh=1' : '';
      const navbarRequest = `/navbar-data-cached?entityId=${this.props.entityId}&userId=${this.props.userId}&userType=${this.props.userType}&access=${this.props.access || 'STANDARD'}${locationParam}${roleParam}${groupParam}${dealerParam}${freshParam}`;
      const { data: navbarData } = await API.get(navbarRequest);

      this.setState({
        requestCount: navbarData.requestCount || 0,
        jobCount: navbarData.jobCount || 0,
        scheduledCount: navbarData.scheduledCount || 0,
        inProgressCount: navbarData.inProgressCount || 0,
        pickupCount: navbarData.pickupCount || 0,
        reviewCount: navbarData.reviewCount || 0,
        referralCount: navbarData.referralCount || 0,
        tasksCount: navbarData.tasksCount || 0,
        authenticated: navbarData.authenticated || false,
        calendar: navbarData.calendar || "",
        isStripeConnected: navbarData.stripeConnected || false,
        loading: false,
        error: null,
        countsReturned: []
      });
    } catch (error) {
      console.error('❌ Optimized navbar loading failed:', error);
      this.setState({ error: error.message, loading: false });
      throw error;
    }
  };

  loadNavbarDataLegacy = async () => {
    try {
      let countRequest = `/counts/?userId=${this.props.entityId}&userType=${this.props.userType}`;
      const { data: countsReturned } = await API.get(countRequest);
      this.setState({ countsReturned });

      if (this.props.userType === "dealer" && this.props.access !== "DIRECT") {
        let referralRequest = `/referralcounts/?userId=${this.props.userId}&userType=${this.props.userType}`;
        const { data: referralsReturned } = await API.get(referralRequest);
        if (referralsReturned.length > 0) {
          let fullCountsReturned = countsReturned.concat(referralsReturned);
          this.setState({ countsReturned: fullCountsReturned });
        }
      }

      var requestCount = 0;
      var jobCount = 0;
      var scheduledCount = 0;
      var inProgressCount = 0;
      var pickupCount = 0;
      var reviewCount = 0;
      var referralCount = 0;

      for (var i = 0; i < this.state.countsReturned.length; i++) {
        switch (this.state.countsReturned[i].current_state) {
          case "referrals":
            referralCount = referralCount + this.state.countsReturned[i].total;
            break;
          case "new":
            requestCount = requestCount + this.state.countsReturned[i].total;
            break;
          case "answered":
            requestCount = requestCount + this.state.countsReturned[i].total;
            break;
          case "scheduled":
            scheduledCount = scheduledCount + this.state.countsReturned[i].total;
            jobCount = jobCount + this.state.countsReturned[i].total;
            break;
          case "received":
            inProgressCount = inProgressCount + this.state.countsReturned[i].total;
            jobCount = jobCount + this.state.countsReturned[i].total;
            break;
          case "started":
            inProgressCount = inProgressCount + this.state.countsReturned[i].total;
            jobCount = jobCount + this.state.countsReturned[i].total;
            break;
          case "finished":
            pickupCount = pickupCount + this.state.countsReturned[i].total;
            break;
          case "collected":
            if (this.props.userType === "dealer") {
              reviewCount = reviewCount + this.state.countsReturned[i].total;
            }
            break;
          default:
            break;
        }
      }

      this.setState({
        requestCount,
        jobCount,
        scheduledCount,
        inProgressCount,
        pickupCount,
        reviewCount,
        referralCount,
        loading: false,
        error: null
      });

    } catch (error) {
      console.error('❌ Legacy navbar loading also failed:', error);
      this.setState({
        error: error.message,
        loading: false,
        requestCount: 0,
        jobCount: 0,
        scheduledCount: 0,
        inProgressCount: 0,
        pickupCount: 0,
        reviewCount: 0,
        referralCount: 0,
        authenticated: false,
        calendar: ""
      });
    }
  };

  readAuth = async (installerId) => {
    let installerRequest = `/installerAuth/${installerId}`;
    const { data: installer } = await API.get(installerRequest);
    this.setState({
      authenticated: installer[0].authenticated,
      calendar: installer[0].calendar_id,
    });
  };

  navigate = async (url) => {
    window.location.href = url;
  };

  authorizeGoogle = async () => {
    let userSetup = `/request`;
    const { data: creds } = await API.get(userSetup);
    this.navigate(creds);
  };

  refreshNavbarData = () => {
    this.setState({ loading: true });
    this.loadOptimizedNavbarData({ force: true }).catch(() => {
      this.loadNavbarDataLegacy();
    });
  };

  isTechnician = () => {
    return this.props.userRole === 'technician';
  };

  isManagerOrOwner = () => {
    const role = this.props.userRole;
    return !role || role === 'manager' || role === 'owner';
  };

  // Section expansion handlers
  handleToggleSection = (section) => {
    this.setState(prev => {
      const newExpanded = {
        ...prev.expandedSections,
        [section]: !prev.expandedSections[section]
      };
      // Persist to localStorage
      saveExpansionState(this.props.entityId, this.props.userRole, newExpanded);
      return { expandedSections: newExpanded };
    });
  };

  // Submenu toggle handler (for Automations)
  handleSubmenuToggle = (itemId) => {
    this.setState(prev => ({
      submenuState: {
        ...prev.submenuState,
        [itemId]: !prev.submenuState[itemId]
      }
    }));
  };

  // Sidebar collapse toggle
  handleToggleSidebar = () => {
    this.setState(prev => {
      const newCollapsed = !prev.sidebarCollapsed;
      saveSidebarCollapsed(this.props.entityId, newCollapsed);
      window.dispatchEvent(new CustomEvent('sidebarToggled', { detail: { collapsed: newCollapsed } }));
      return { sidebarCollapsed: newCollapsed };
    });
  };

  // Build badge counts object for NavSection
  getBadgeCounts = () => {
    return {
      leads_actionable: this.state.referralCount,
      requestimates_actionable: this.state.requestCount,
      jobs_actionable: this.state.jobCount,
      scheduled_actionable: this.state.scheduledCount || 0,
      inprogress_actionable: this.state.inProgressCount || 0,
      pickupdeliver_actionable: this.state.pickupCount,
      pickups_actionable: this.state.pickupCount,
      referrals_actionable: this.state.referralCount,
      reviews_actionable: this.state.reviewCount,
      mywork_actionable: this.state.myWorkCount,
      tasks_actionable: this.state.tasksCount
    };
  };

  // Build warnings object for NavSection
  getWarnings = () => {
    // Calendar warning only applies at location level (calendars are per-location)
    const atLocationLevel = this.props.selectedLocationId && this.props.selectedLocationId !== 'all';
    return {
      calendarNotConnected: atLocationLevel && !this.state.isCalendarConnected,
      stripeNotConnected: !this.state.isStripeConnected
    };
  };

  renderExpandedLogo = (jjDemo) => {
    const { customLogoUrl } = this.state;
    if (customLogoUrl) {
      return <img src={customLogoUrl} alt="Company Logo" className="sidebar-custom-logo mb-3 mt-3" />;
    }
    if (jjDemo) {
      return <img src={JJLogoSeparate} alt="Jobvious" width="200px" className="mb-0 mt-1" />;
    }
    return <img src={JobviousLogo} alt="Jobvious" width="200px" className="mb-3 mt-3" />;
  };

  render() {
    const { sidebarCollapsed } = this.state;

    const jjDemo = isJJDemoUser();

    // Loading state
    if (this.state.loading) {
      return (
        <React.Fragment>
          <div className={`main-sidebar bg-primary-orange ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="d-flex align-items-center justify-content-center">
              {sidebarCollapsed ? (
                <img
                  src={jjDemo ? JJLogoOverlapping : JobviousFavicon}
                  alt="J"
                  className={jjDemo ? "sidebar-jj-icon my-2" : "sidebar-favicon my-3"}
                />
              ) : (
                this.renderExpandedLogo(jjDemo)
              )}
            </div>
            {!sidebarCollapsed && (
              <div className="text-center text-light">
                <small>Loading...</small>
              </div>
            )}
          </div>
        </React.Fragment>
      );
    }

    // Error state
    if (this.state.error) {
      return (
        <React.Fragment>
          <div className={`main-sidebar bg-primary-orange ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="d-flex align-items-center justify-content-center">
              {sidebarCollapsed ? (
                <img
                  src={jjDemo ? JJLogoOverlapping : JobviousFavicon}
                  alt="J"
                  className={jjDemo ? "sidebar-jj-icon my-2" : "sidebar-favicon my-3"}
                />
              ) : (
                this.renderExpandedLogo(jjDemo)
              )}
            </div>
            {!sidebarCollapsed && (
              <div className="text-center text-light">
                <small>Error loading menu</small>
                <button
                  className="btn btn-sm btn-light mt-2"
                  onClick={this.refreshNavbarData}
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </React.Fragment>
      );
    }

    // Get menu for current user role
    const menu = getMenuForRole(
      this.props.userType,
      this.props.userRole,
      this.props.access,
      { technicianCanActionLeads: true } // TODO: Get from company settings
    );

    const badgeCounts = this.getBadgeCounts();
    const warnings = this.getWarnings();

    return (
      <React.Fragment>
        <div className={`main-sidebar bg-primary-orange ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          {/* Logo — click to toggle sidebar */}
          <div
            className="sidebar-logo-toggle d-flex align-items-center justify-content-center"
            onClick={this.handleToggleSidebar}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? (
              <img
                src={jjDemo ? JJLogoOverlapping : JobviousFavicon}
                alt="J"
                className={jjDemo ? "sidebar-jj-icon my-2" : "sidebar-favicon my-3"}
              />
            ) : (
              this.renderExpandedLogo(jjDemo)
            )}
          </div>

          {/* Config-driven navigation sections */}
          {SECTION_ORDER.map(section => {
            const items = menu[section];
            if (!items || items.length === 0) return null;

            return (
              <NavSection
                key={section}
                section={section}
                items={items}
                isExpanded={this.state.expandedSections[section]}
                onToggle={this.handleToggleSection}
                counts={badgeCounts}
                warnings={warnings}
                userType={this.props.userType}
                submenuState={this.state.submenuState}
                onSubmenuToggle={this.handleSubmenuToggle}
                collapsed={sidebarCollapsed}
              />
            );
          })}

          {/* Sign Out */}
          {sidebarCollapsed ? (
            <div className="nav-item-collapsed mt-3" title="Sign Out" onClick={this.doSubmit}>
              <div className="nav-collapsed-link" style={{ cursor: 'pointer' }}>
                <i className="fa-sharp fa-regular fa-right-from-bracket"></i>
              </div>
            </div>
          ) : (
            <div className="mt-5 mb-5 text-center">
              <button
                className="btn btn-success btn-primary-green rounded-pill"
                onClick={this.doSubmit}
              >
                SIGN OUT
              </button>
            </div>
          )}

          {/* Footer (expanded only) */}
          {!sidebarCollapsed && (
            <>
              {process.env.NODE_ENV === 'development' && (
                <div className="version text-center text-light">
                  <small>
                    R:{this.state.requestCount} J:{this.state.jobCount} P:{this.state.pickupCount}
                  </small>
                </div>
              )}

              <div className="version text-center text-light">
                v {packageJSON.version}
              </div>
              <div className="version text-center">
                <a
                  className="text-light"
                  href="https://s3.us-east-2.amazonaws.com/jobvious.digitalassets/Jobvious_Release_Notes.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Release Notes
                </a>
              </div>
              <div className="version text-center">
                <a
                  className="text-light"
                  href="https://s3.us-east-2.amazonaws.com/jobvious.digitalassets/Jobvious.Privacy.Policy.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </div>
              <div className="version text-center">
                <a
                  className="text-light"
                  href="https://s3.us-east-2.amazonaws.com/jobvious.digitalassets/Jobvious.Limited.Use.Policy.7.10.24.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google API Policy
                </a>
              </div>
            </>
          )}

        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(navBar);
